'use strict';
import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import * as T from 'prop-types';
import React, {PureComponent} from 'react';
import {Subject} from 'rxjs';
import 'rxjs/add/observable/merge';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/delay';
import 'rxjs/add/operator/distinctUntilChanged';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/switchMap';
import {Observable} from 'rxjs/Observable';
import {getId} from '../../../core-utilities/domain/ReduxUtils';
import {logger} from '../logger';


export class DomainImageComponent extends PureComponent {
  static propTypes = {
    placeholder: T.string,
    file: T.object,
    type: T.string, /* tb or full */
    targetWidth: T.number, /* required if type == tb, ignored otherwise */
    targetHeight: T.number, /* required if type == tb, ignored otherwise */
    displayWidth: T.number,
    displayHeight: T.number,
    cropTarget: T.bool,
    className: T.string,
  };

  static defaultState = () => ({
    source: null,
    domainImageLoaded: false,
    domainImageLoading: false,
    showPlaceholder: false,
  });

  static $inject = [
    'FileUrlCache',
  ];

  constructor(props) {
    super(props);
    this.state = DomainImageComponent.defaultState();
    this.state.style = DomainImageComponent.getStyle(props);
  }

  render() {
    const src = this.state.showPlaceholder && this.props.placeholder ? this.props.placeholder : this.state.source;
    // TODO: implement loading behavior
    // We probably need to communicate state to parents with callbacks
    return (
      <img
        className={classNames('domain-image', this.props.className)}
        style={this.state.style}
        src={src}
      />
    );
  }

  static getFileId(props) {
    return getId(props.file);
  }

  static getStyle(props) {
    const style = {};

    if (props.displayWidth) {
      style.width = `${props.displayWidth}px`;
    } else if (props.targetWidth) {
      style.width = `${props.targetWidth}px`;
    }

    if (props.displayHeight) {
      style.height = `${props.displayHeight}px`;
    } else if (props.targetHeight) {
      style.height = `${props.targetHeight}px`;
    }

    return style;
  }

  withNextFileId(props) {
    const fileId = DomainImageComponent.getFileId(props);
    if (this.fileId !== fileId && this.fileId$) {
      this.fileId = fileId;
      this.fileId$.next(fileId);
    }
  }

  componentWillReceiveProps(nextProps) {
    const nextStyle = DomainImageComponent.getStyle(nextProps);
    if (nextStyle.width !== this.state.style.width || nextStyle.height !== this.state.style.height) {
      this.setState({style: nextStyle});
    }

    this.withNextFileId(nextProps);
  }

  componentDidMount() {
    this.fileId$ = new Subject(1);

    this.stateSubscription = this.fileId$
      .switchMap(props => DomainImageComponent.onNewFileId(props.file, props.type, props))
      .subscribe(nextState => this.setState(nextState));

    this.withNextFileId(this.props);
  }

  componentWillUnmount() {
    this.fileId$ = null;
    this.stateSubscription && this.stateSubscription.unsubscribe();
  }

  static onNewFileId(file, type, props) {
    if (file) {
      const fileDataUrl = DomainImageComponent.dependencies.FileUrlCache.get(getId(file));

      if (fileDataUrl && fileDataUrl.url) {
        return Observable.of({
          showPlaceholder: false,
          domainImageLoaded: true,
          domainImageLoading: false,
          source: fileDataUrl.url,
        });
      } else if (file.s3Status === 'available') {
        return Observable.merge(
          Observable.of({
            domainImageLoaded: false,
            domainImageLoading: true,
          }),
          DomainImageComponent.fetchImage(file, type, props),
        );
      } else {
        return Observable.merge(
          Observable.of({
            domainImageLoaded: false,
            domainImageLoading: true,
          }),
          DomainImageComponent.initWatch(file, type, props),
        );
      }
    } else {
      return Observable.of({
        showPlaceholder: true,
      });
    }
  }

  static initWatch(file, type, props) {
    return file.$observe()
      .filter(file => file.s3Status === 'available')
      .take(1)
      .switchMap(file => DomainImageComponent.fetchImage(file, type, props));
  }

  // TODO add support for domainImageSize attr so we can fetch larger images
  static fetchImage(file, type, props) {
    if (type === 'full') {
      // TODO: set a max size and request a preview up to that bounding box. Never show large full im
      return file.getUrl()
        .then(url => ({
          domainImageLoaded: true,
          domainImageLoading: false,
          showPlaceholder: false,
          source: url,
        }))
        .catch(DomainImageComponent.onError);
    } else {
      const targetWidth = isNumber(props.targetWidth) ? props.targetWidth : parseInt(props.targetWidth, 10);
      const targetHeight = isNumber(props.targetHeight) ? props.targetHeight : parseInt(props.targetHeight, 10);

      if (isNaN(targetHeight) || isNaN(targetWidth)) {
        // eslint-disable-next-line max-len
        return Promise.resolve(DomainImageComponent.onError(new Error('Invalid height or width provided. Expected an integer or a string parsable as a integer')));
      } else {
        return file.getThumbnailUrl({
          targetWidth: targetWidth,
          targetHeight: targetHeight,
          /*    ****************** Legacy angular support ****************** || ***** React Support ***** */
          /* eslint-disable-next-line max-len */
          type: isUndefined(props.cropTarget) || props.cropTarget === 'true' || props.cropTarget === true ? 'crop' : 'fit',
        })
          .then(url => ({
            domainImageLoaded: true,
            domainImageLoading: false,
            showPlaceholder: false,
            source: url,
          }))
          .catch(DomainImageComponent.onError);
      }
    }
  }

  static onError(err) {
    logger.error({err}, 'domainImageLoaded with errors');
    return {
      domainImageLoaded: true,
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/files/components/DomainImage/DomainImageComponent.js